export const BROWNFIELD_BRIDGE_DEEPLINK_TYPE = {
  episode: 'episode',
  services: 'services',
  physicalHealth: 'physicalHealth',
  mentalHealth: 'mentalHealth',
  diabetesCare: 'diabetesCare',
  respiratoryCare: 'respiratoryCare',
  quitSmoking: 'quitSmoking',
  ePharmacy: 'ePharmacy',
  ePharmacyTrackPrescriptionOrder: 'ePharmacyTrackPrescriptionOrder',
  ePharmacyAddPrescription: 'ePharmacyAddPrescription',
  ePharmacyManageMedications: 'ePharmacyManageMedications',
  ePharmacyChatPharmacy: 'ePharmacyChatPharmacy',
  ePharmacyCarePlan: 'ePharmacyCarePlan',
  ePharmacyConsultation: 'ePharmacyConsultation',
  pushOptIn: 'pushOptIn',
  profile: 'profile',
  addFamilyMember: 'addFamilyMember',
  today: 'today',
  inbox: 'inbox',
  account: 'account',
} as const

export const BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE = {
  DeeplinkOpened: 'DEEPLINK_OPENED',
  DeeplinkRejected: 'DEEPLINK_REJECTED',
  DeeplinkDeferred: 'DEEPLINK_DEFERRED',
  DeeplinkInvalid: 'DEEPLINK_INVALID',
} as const

export interface DeeplinkEventPayload {
  requestId: string
  callingUrl?: string
  episodeId?: string
}

/**
 * Event triggered when a deeplink is successfully handled on the JS side.
 */

export interface DeeplinkOpened {
  type: typeof BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE.DeeplinkOpened
  payload?: DeeplinkEventPayload
}

/**
 * Event emitted when a deeplink cannot be handled or an internal error occurred.
 */

export interface DeeplinkRejected {
  type: typeof BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE.DeeplinkRejected
  payload?: DeeplinkEventPayload
}

/**
 * Event emitted when a deeplink cannot be formed from the payload sent.
 */

export interface DeeplinkInvalid {
  type: typeof BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE.DeeplinkInvalid
}

/**
 * Event emitted when a deeplink is valid but processing is postponed.
 * Typical causes: cold start, auth gate, required resources not ready, or
 * waiting for navigation container to mount.
 */
export interface DeeplinkDeferred {
  type: typeof BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE.DeeplinkDeferred
  payload?: DeeplinkEventPayload
}

export type DeeplinkTypeKeys =
  (typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE)[keyof typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE]

export type BrownfieldBridgeDeeplink =
  | {
      type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.episode
      requestId: string
      payload: { episodeId: string }
    }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.services; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.physicalHealth; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.mentalHealth; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.diabetesCare; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.respiratoryCare; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.quitSmoking; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.ePharmacy; requestId: string }
  | {
      type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.ePharmacyTrackPrescriptionOrder
      requestId: string
    }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.ePharmacyAddPrescription; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.ePharmacyManageMedications; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.ePharmacyChatPharmacy; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.ePharmacyCarePlan; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.ePharmacyConsultation; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.pushOptIn; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.profile; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.addFamilyMember; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.today; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.inbox; requestId: string }
  | { type: typeof BROWNFIELD_BRIDGE_DEEPLINK_TYPE.account; requestId: string }

export type BrownfieldBridgeDeeplinkEvent =
  | DeeplinkOpened
  | DeeplinkRejected
  | DeeplinkDeferred
  | DeeplinkInvalid
